<script setup lang="ts">
/**
 * Témoignages hébergeurs (spec 4.98.0).
 *
 * Deux registres ÉTANCHES, et c'est la règle centrale du composant :
 *
 *  - `acquisition` — l'hébergeur a confirmé que les demandes venaient bien de la
 *    visibilité de la plateforme. Seul ce registre peut afficher un chiffre.
 *  - `adhesion`    — avis sur le modèle ou le produit. JAMAIS de chiffre, même si
 *    une clé `metric` traînait dans les JSON i18n.
 *
 * Pourquoi c'est verrouillé ici plutôt que laissé à la relecture : sur les quatre
 * premières réponses reçues, deux hébergeurs ont indiqué eux-mêmes que leurs
 * réservations venaient de leur propre audience (site perso, mailing clients) et
 * non d'Holicosy. Leur attribuer un chiffre d'acquisition serait faux. Le registre
 * rend la faute structurellement impossible au lieu de dépendre de la vigilance du
 * rédacteur.
 *
 * Le registre n'est PAS traduisible — il décrit la nature de l'accord obtenu, pas
 * le texte. Il vit donc ici, en TypeScript, et pas dans les JSON i18n.
 *
 * Ajouter un témoignage = une entrée ci-dessous + les clés correspondantes sous
 * `pages.home.testimonials.items.<key>` en FR ET en EN. Un nom n'est publié qu'une
 * fois l'accord écrit obtenu.
 */
type TestimonialRegister = 'acquisition' | 'adhesion'

interface TestimonialEntry {
  key: string
  register: TestimonialRegister
  /**
   * Chemin de la fiche publique, `/{codeSlug}/{slug}` (spec 4.99.0). Rend le
   * témoignage vérifiable — un nom seul n'est qu'une affirmation — et alimente
   * le maillage interne vers les fiches.
   *
   * En dur plutôt que résolu à l'exécution : ces quatre fiches sont du contenu
   * éditorial figé, une requête serait du travail pour rien. Contrepartie
   * assumée : un slug qui change casse le lien. Les slugs suivent le nom de
   * l'établissement (4.64.0), donc à revérifier si un propriétaire renomme sa
   * fiche.
   *
   * `null` si la fiche n'est pas publiée : mieux vaut pas de lien qu'un 404.
   */
  path: string | null
}

// Chemins vérifiés en HTTP 200 sur holicosy.fr le 2026-08-15, statuts PUBLISHED
// contrôlés en base de PRODUCTION — la base locale donnait un DRAFT périmé sur
// La P'tite Transhumance.
const TESTIMONIALS: readonly TestimonialEntry[] = [
  { key: 'ptitToy', register: 'acquisition', path: '/65-hautes-pyrenees/le-p-tit-toy' },
  { key: 'vieilleForge', register: 'acquisition', path: '/80-somme/la-vieille-forge-chambres-d-hotes' },
  { key: 'ptiteTranshumance', register: 'acquisition', path: '/50-manche/la-p-tite-transhumance' },
  { key: 'auFondDuTerroir', register: 'adhesion', path: '/39-jura/au-fond-du-terroir-a-gauche' },
] as const

const { t, te } = useI18n()
const localePath = useLocalePath()

const itemKey = (key: string, field: string) => `pages.home.testimonials.items.${key}.${field}`

interface RenderedTestimonial {
  key: string
  quote: string
  author: string
  location: string | null
  /** Toujours `null` hors registre `acquisition`. */
  metric: string | null
  /** `null` quand la fiche n'est pas publiée : pas de lien plutôt qu'un 404. */
  href: string | null
}

const testimonials = computed<RenderedTestimonial[]>(() =>
  TESTIMONIALS.map(entry => ({
    key: entry.key,
    quote: t(itemKey(entry.key, 'quote')),
    author: t(itemKey(entry.key, 'author')),
    location: te(itemKey(entry.key, 'location')) ? t(itemKey(entry.key, 'location')) : null,
    href: entry.path ? localePath(entry.path) : null,
    // Le registre prime sur la présence de la clé : une entrée `adhesion` ne peut
    // pas afficher de chiffre, même par erreur de contenu.
    metric: entry.register === 'acquisition' && te(itemKey(entry.key, 'metric'))
      ? t(itemKey(entry.key, 'metric'))
      : null,
  })),
)

/**
 * Taille de l'échantillon chiffré, DÉRIVÉE des témoignages rendus.
 *
 * La mention d'échantillon annonçait « deux hébergements » en dur dans les JSON
 * i18n. L'ajout d'un troisième témoignage d'acquisition (La P'tite Transhumance,
 * 4.98.1) ne l'a pas mise à jour : la phrase censée empêcher une lecture erronée
 * des chiffres est devenue elle-même fausse. Compter ici rend la divergence
 * impossible — ajouter ou retirer une entrée `acquisition` corrige la mention
 * sans que personne ait à y penser.
 *
 * Zéro chiffre affiché ⇒ pas de mention : elle n'aurait rien à qualifier.
 */
const metricCount = computed(() => testimonials.value.filter(item => item.metric !== null).length)

// Garde-fou de contenu : une clé `metric` sous une entrée `adhesion` est une erreur
// de rédaction. Elle est déjà neutralisée à l'affichage, on la signale en dev pour
// qu'elle soit corrigée à la source plutôt que de dormir dans les JSON.
if (import.meta.dev) {
  for (const entry of TESTIMONIALS) {
    if (entry.register === 'adhesion' && te(itemKey(entry.key, 'metric'))) {
      console.warn(
        `[testimonials] "${entry.key}" est en registre "adhesion" mais porte une clé "metric" : `
        + 'un témoignage sans attribution confirmée ne doit pas citer de chiffre. Clé ignorée à l\'affichage.',
      )
    }
  }
}
</script>

<template>
  <section
    v-if="testimonials.length > 0"
    id="testimonials"
    class="py-20 md:py-28 bg-paper-100 border-y border-paper-300/60"
  >
    <div class="container mx-auto px-4">
      <div class="text-center max-w-3xl mx-auto mb-12">
        <p class="tag text-sage-700 mb-4 justify-center">
          {{ $t('pages.home.testimonials.eyebrow') }}
        </p>
        <h2 class="font-display font-light text-display-sm md:text-display-md leading-tight tracking-[-0.02em] text-paper-900 mb-4">
          {{ $t('pages.home.testimonials.title') }}
        </h2>
        <p class="text-paper-900/70 text-base font-sans leading-relaxed">
          {{ $t('pages.home.testimonials.subtitle') }}
        </p>
      </div>

      <!--
        Grille statique, sans carrousel : pas d'auto-défilement ni de contenu masqué
        derrière une interaction (WCAG AA, cible senior).
      -->
      <ul class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        <li
          v-for="item in testimonials"
          :key="item.key"
          class="flex flex-col rounded-2xl border hairline bg-paper-50 p-8"
        >
          <blockquote class="flex-1 text-paper-900/85 text-base leading-relaxed font-sans">
            {{ item.quote }}
          </blockquote>
          <footer class="mt-6 pt-5 border-t border-paper-300/60">
            <!--
              Lié à la fiche quand elle est publiée : le témoignage devient
              vérifiable, et un futur propriétaire voit à quoi ressemble une fiche.
              Le nom seul reste affiché si `href` est nul.
            -->
            <cite class="not-italic font-medium text-paper-900 text-sm">
              <NuxtLink
                v-if="item.href"
                :to="item.href"
                data-testid="testimonial-link"
                class="underline decoration-sage-300 underline-offset-4 hover:decoration-sage-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 transition-colors cursor-pointer"
              >
                {{ item.author }}
              </NuxtLink>
              <template v-else>{{ item.author }}</template>
            </cite>
            <p v-if="item.location" class="text-sm text-paper-900/60 font-sans mt-0.5">
              {{ item.location }}
            </p>
            <p
              v-if="item.metric"
              data-testid="testimonial-metric"
              class="mt-3 font-mono text-[11px] uppercase tracking-[0.1em] text-sage-700"
            >
              {{ item.metric }}
            </p>
          </footer>
        </li>
      </ul>

      <p
        v-if="metricCount > 0"
        data-testid="testimonial-sample-note"
        class="text-xs text-paper-900/55 font-sans text-center max-w-2xl mx-auto mt-10 leading-relaxed"
      >
        {{ $t('pages.home.testimonials.sampleNote', { count: metricCount }, metricCount) }}
      </p>
    </div>
  </section>
</template>
